// Cómo genera el Catálogo (ADR-2026-10-14-la-sesion-de-catalogo-muestra-la-tanda-como-el-sheet):
// el plan se parte en lotes de ≤ 100 imágenes (`armarLotes`) y cada uno va a `/v1/agent/lote`
// como el lote del chat, EN SERIE (dos a la vez pasan el tope de jobs por empresa). Cada lote
// es una tanda de la columna derecha (`useTandasPanel`): se crea, se registran sus
// `execution_id`, se polea y se cierra con el preload. Las filas que llegan vuelven a su ítem por
// `execution_id` (`aplicarFilas`). Si la pestaña se cierra, `reanudarLotes` vuelve a polear los
// lotes que el plan guardó sin terminar.
import { signReferenceImage } from '../../agentAttachments';
import { atlasBackendFetch } from '../../../../lib/atlasBackend';
import { creditosPorResolucion } from '../../../../lib/resolucionImagen';
import { firmarEnLote } from '../../../../utils/firmarEnLote';
import { preloadGeneratedImages } from '../../../../utils/preloadGeneratedImages';
import { fetchInChunks } from '../../../../utils/supabaseChunkedIn';
import { creditosComprometidos, generacionesStore, timeoutPollTanda } from '../../generacionesEnCurso';
import { ANUNCIO_STABLE_ROUNDS, pollGeneratedByExecutionId } from '../../pollGeneratedByExecutionId';
import type { TandasPanelApi } from '../../useGeneracionesEnCurso';
import { ratioDelFormatoElegido } from '../promptLibre/promptLibre';
import { aplicarFilas, armarLotes, aspectDelFormato, cerrarLote, marcarLanzados, type LoteArmado, type LoteLanzado, type PlanDeCatalogo, type ReferenciaDelPlan } from './plan';

export type TandaApi = Pick<TandasPanelApi, 'crearTanda' | 'registrarExecutionIds' | 'cerrarTanda' | 'fallarTanda'>;

/** Corre las tareas de a una, en orden. Con la señal abortada no arranca la siguiente. */
export async function secuenciar<T>(tareas: Array<() => Promise<T>>, signal?: AbortSignal): Promise<T[]> {
  const out: T[] = [];
  for (const t of tareas) {
    if (signal?.aborted) break;
    out.push(await t());
  }
  return out;
}

export const lotesPendientes = (plan: PlanDeCatalogo): LoteLanzado[] =>
  plan.lotes.filter((l) => !l.terminado && l.executionIds.length > 0);

export type ResultadoDeGeneracion =
  | { ok: true; plan: PlanDeCatalogo; lanzados: number }
  | { ok: false; error: string; sinCreditos: boolean; plan: PlanDeCatalogo; lanzados: number };

interface ParamsDeGeneracion {
  plan: PlanDeCatalogo;
  companyId: string;
  chatId: string;
  tanda: TandaApi;
  /** Cada cambio del plan (lanzado, filas que llegan, cierre). */
  onPlan: (plan: PlanDeCatalogo) => void;
  soloItems?: string[];
  signal?: AbortSignal;
  /** Se salió de la pantalla: no arranca un lote nuevo. El que está en vuelo sigue (cortar su
   *  POST podría dejar un lote lanzado sin anotar), y lo que falta queda "por lanzar" en el plan. */
  noLanzarMas?: AbortSignal;
}

const urlDeReferencia = async (r: ReferenciaDelPlan): Promise<string | null> =>
  signReferenceImage(r.storagePath, r.bucket).catch(() => null);

/** Las referencias del plan, firmadas una vez para todos los lotes. */
async function firmarReferencias(plan: PlanDeCatalogo): Promise<(r: ReferenciaDelPlan) => string | null> {
  const firmadas = new Map<string, string | null>();
  await Promise.all(plan.referencias.map(async (r) => firmadas.set(r.id, await urlDeReferencia(r))));
  return (r) => firmadas.get(r.id) ?? null;
}

/**
 * Las fotos base de las ediciones pendientes, firmadas en UN pedido (el `full/`, sin bajarlas al
 * navegador): `imageId → URL`. Una base que no se pudo firmar no está y su edición no sale
 * (ADR-2026-10-14-el-agente-edita-el-catalogo-en-masa).
 */
async function firmarBasesDeEdicion(plan: PlanDeCatalogo, companyId: string, soloItems?: string[]): Promise<(imageId: string) => string | null> {
  if (soloItems) return () => null;
  const ids = [...new Set(plan.productos.flatMap((p) => p.items.flatMap((i) => (i.edicion ? [i.edicion.baseImageId] : []))))];
  if (ids.length === 0) return () => null;
  try {
    const firmadas = await firmarOriginales(ids, companyId);
    return (imageId) => firmadas.get(imageId) ?? null;
  } catch {
    return () => null;
  }
}

/** Los originales (`full/`) de unas creaciones, firmados en UN pedido: `imageId → URL`. Las que
 *  no tienen archivo o no se pudieron firmar no están. Lo usan las ediciones y la descarga del
 *  catálogo (ADR-2026-10-15-el-catalogo-se-descarga-y-se-lanza). */
export async function firmarOriginales(ids: readonly string[], companyId: string): Promise<Map<string, string>> {
  const { supabase } = await import('../../../../lib/supabase');
  const filas = await fetchInChunks<{ id: string; file_name: string | null }>([...ids], (chunk) =>
    supabase.from('imagenes_generadas').select('id, file_name').eq('id_company', companyId).in('id', chunk),
  );
  const pathDe = new Map(filas.filter((f) => f.file_name).map((f) => [String(f.id), `${companyId}/full/${f.file_name}`]));
  const firmas = await firmarEnLote([...pathDe.values()]);
  const out = new Map<string, string>();
  for (const [id, path] of pathDe) {
    const url = firmas.get(path);
    if (url) out.set(id, url);
  }
  return out;
}

/**
 * Polea un lote ya lanzado hasta que termine, volcando cada fila a su ítem, y cierra su tanda.
 * El lote queda terminado sólo si no le quedó nada generando (`cerrarLote`).
 */
async function esperarLote(p: {
  plan: PlanDeCatalogo;
  jobId: string;
  executionIds: string[];
  total: number;
  companyId: string;
  idTanda: string | null;
  tanda: TandaApi;
  onPlan: (plan: PlanDeCatalogo) => void;
  signal?: AbortSignal;
}): Promise<PlanDeCatalogo> {
  let plan = p.plan;
  const filas = await pollGeneratedByExecutionId({
    executionId: p.executionIds,
    companyId: p.companyId,
    expectedCount: p.total,
    timeoutMs: timeoutPollTanda(p.total),
    stableRounds: ANUNCIO_STABLE_ROUNDS,
    signal: p.signal,
    onProgreso: (rows) => {
      plan = aplicarFilas(plan, rows);
      p.onPlan(plan);
    },
  });
  plan = aplicarFilas(plan, filas);
  plan = cerrarLote(plan, p.jobId);
  p.onPlan(plan);
  if (p.idTanda) {
    const ok = filas.filter((f) => !f.has_error).map((f) => f.id);
    const preloaded = ok.length > 0 ? await preloadGeneratedImages(ok, p.companyId).catch(() => []) : [];
    if (ok.length === 0) p.tanda.fallarTanda(p.idTanda);
    else p.tanda.cerrarTanda(p.idTanda, preloaded);
  }
  return plan;
}

/** Lanza UN lote: crea la tanda, POST, marca los ítems, espera. */
async function lanzarLote(p: ParamsDeGeneracion & { lote: LoteArmado; plan: PlanDeCatalogo }): Promise<{ plan: PlanDeCatalogo; error?: string; sinCreditos?: boolean }> {
  const idTanda = p.tanda.crearTanda({
    total: p.lote.total,
    ratio: ratioDelFormatoElegido(p.plan.formato),
    producto: p.plan.titulo,
    modo: 'catalogo-agente',
    creditos: p.lote.total * creditosPorResolucion(p.plan.resolucion),
  });
  let json: { job_id?: string; items?: Array<{ id: string; execution_id: string }>; error?: string } = {};
  let status = 0;
  try {
    const res = await atlasBackendFetch(
      '/v1/agent/lote',
      {
        items: p.lote.items,
        formato: aspectDelFormato(p.plan.formato),
        image_size: p.plan.resolucion,
        titulo: p.plan.titulo,
        agent_chat_id: p.chatId,
      },
      p.signal ? { signal: p.signal } : undefined,
    );
    status = res.status;
    json = (await res.json().catch(() => ({}))) as typeof json;
  } catch {
    if (idTanda) p.tanda.fallarTanda(idTanda);
    return { plan: p.plan, error: 'No pude conectarme para lanzar el catálogo. Probá de nuevo.' };
  }
  if (!json.job_id || !Array.isArray(json.items)) {
    if (idTanda) p.tanda.fallarTanda(idTanda);
    if (status === 402) return { plan: p.plan, error: 'No te alcanzan los créditos para este catálogo.', sinCreditos: true };
    return { plan: p.plan, error: json.error ? `No se pudo lanzar el catálogo: ${json.error}` : 'No se pudo lanzar el catálogo. Probá de nuevo.' };
  }
  const respuesta = { job_id: json.job_id, items: json.items };
  let plan = marcarLanzados(p.plan, p.lote, respuesta);
  p.onPlan(plan);
  const executionIds = respuesta.items.map((i) => i.execution_id);
  if (idTanda) p.tanda.registrarExecutionIds(idTanda, executionIds, p.lote.total);
  plan = await esperarLote({ plan, jobId: respuesta.job_id, executionIds, total: p.lote.total, companyId: p.companyId, idTanda, tanda: p.tanda, onPlan: p.onPlan, signal: p.signal });
  return { plan };
}

/** El saldo fresco menos lo comprometido por las tandas en curso. `null` si no se pudo leer (el backend chequea igual). */
async function saldoDisponible(companyId: string): Promise<number | null> {
  try {
    const { supabase } = await import('../../../../lib/supabase');
    const { data } = await supabase.from('company_profiles').select('imagenes_restantes:imagenes_disponibles').eq('id_company', companyId).single();
    const saldo = Number((data as { imagenes_restantes?: number } | null)?.imagenes_restantes ?? 0);
    return saldo - creditosComprometidos(generacionesStore.get(), companyId);
  } catch {
    return null;
  }
}

/** Genera el plan entero (o `soloItems`) en lotes en serie. Nunca tira. */
export async function generarCatalogo(p: ParamsDeGeneracion): Promise<ResultadoDeGeneracion> {
  let plan = p.plan;
  const firmar = await firmarReferencias(plan);
  const base = await firmarBasesDeEdicion(plan, p.companyId, p.soloItems);
  const lotes = armarLotes(plan, firmar, p.soloItems, base);
  if (lotes.length === 0) {
    const hayEdiciones = !p.soloItems && plan.productos.some((pr) => pr.items.some((i) => i.edicion));
    const error = hayEdiciones ? 'No pude preparar las fotos a editar. Probá de nuevo.' : 'No hay nada para generar: faltan los prompts.';
    return { ok: false, error, sinCreditos: false, plan, lanzados: 0 };
  }
  const creditos = lotes.reduce((a, l) => a + l.total, 0) * creditosPorResolucion(plan.resolucion);
  const saldo = await saldoDisponible(p.companyId);
  if (saldo !== null && saldo < creditos) {
    return { ok: false, error: `No te alcanzan los créditos: el catálogo necesita ${creditos} y tenés ${Math.max(0, saldo)} disponibles.`, sinCreditos: true, plan, lanzados: 0 };
  }
  let lanzados = 0;
  let error: { error: string; sinCreditos: boolean } | null = null;
  await secuenciar(
    lotes.map((lote) => async () => {
      if (error || p.noLanzarMas?.aborted) return;
      const r = await lanzarLote({ ...p, plan, lote });
      plan = r.plan;
      if (r.error) error = { error: r.error, sinCreditos: !!r.sinCreditos };
      else lanzados += lote.total;
    }),
    p.signal,
  );
  if (error) return { ok: false, ...(error as { error: string; sinCreditos: boolean }), plan, lanzados };
  return { ok: true, plan, lanzados };
}

/** Al abrir la sesión: los lotes que el plan guardó sin terminar se vuelven a polear (sin POST). */
export async function reanudarLotes(p: Omit<ParamsDeGeneracion, 'soloItems'>): Promise<PlanDeCatalogo> {
  let plan = p.plan;
  for (const lote of lotesPendientes(plan)) {
    if (p.signal?.aborted) break;
    const idTanda = p.tanda.crearTanda({
      total: lote.total,
      ratio: ratioDelFormatoElegido(plan.formato),
      producto: plan.titulo,
      modo: 'catalogo-agente',
      executionIds: lote.executionIds,
    });
    plan = await esperarLote({ plan, jobId: lote.jobId, executionIds: lote.executionIds, total: lote.total, companyId: p.companyId, idTanda, tanda: p.tanda, onPlan: p.onPlan, signal: p.signal });
  }
  return plan;
}
